import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Button } from './ui/button';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3, List, ListOrdered, Quote,
  Link as LinkIcon, Image as ImageIcon, Undo, Redo,
  AlignLeft, AlignCenter, AlignRight, FileCode2, Eye,
} from 'lucide-react';

/**
 * RichTextEditor — WYSIWYG editor with HTML source toggle.
 * - Preserves HTML markup for proposal formatting.
 * - "HTML" button flips to raw HTML editing for power users.
 */
export default function RichTextEditor({ value, onChange, placeholder = 'Start writing…', minHeight = 200 }) {
  const [mode, setMode] = useState('wysiwyg'); // 'wysiwyg' | 'html'
  const [html, setHtml] = useState(value || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener', target: '_blank' } }),
      Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'rounded-md max-w-full' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: `prose prose-sm dark:prose-invert max-w-none focus:outline-none p-3 min-h-[${minHeight}px]`,
        'data-testid': 'rte-content',
      },
    },
    onUpdate: ({ editor }) => {
      const newHtml = editor.getHTML();
      setHtml(newHtml);
      onChange?.(newHtml);
    },
  });

  // Sync when external value changes (e.g. loading existing proposal)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
      setHtml(value || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const applyHtml = useCallback(() => {
    editor?.commands.setContent(html, false);
    onChange?.(html);
  }, [editor, html, onChange]);

  const addLink = () => {
    const url = window.prompt('Link URL (include https://)');
    if (url) editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  const addImage = () => {
    const url = window.prompt('Image URL (must be https)');
    if (url) editor?.chain().focus().setImage({ src: url }).run();
  };

  if (!editor) return <div className="text-xs text-muted-foreground">Loading editor…</div>;

  const Btn = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-muted transition ${active ? 'bg-indigo-500/15 text-indigo-500' : 'text-foreground'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-border rounded-lg bg-background overflow-hidden" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 flex-wrap p-1.5 border-b border-border bg-muted/30">
        <Btn title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}><Bold className="w-4 h-4" /></Btn>
        <Btn title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}><Italic className="w-4 h-4" /></Btn>
        <Btn title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')}><UnderlineIcon className="w-4 h-4" /></Btn>
        <Btn title="Strike" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')}><Strikethrough className="w-4 h-4" /></Btn>
        <Btn title="Inline Code" onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')}><Code className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn title="Heading 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })}><Heading1 className="w-4 h-4" /></Btn>
        <Btn title="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}><Heading2 className="w-4 h-4" /></Btn>
        <Btn title="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}><Heading3 className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}><List className="w-4 h-4" /></Btn>
        <Btn title="Ordered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}><ListOrdered className="w-4 h-4" /></Btn>
        <Btn title="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}><Quote className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn title="Align left" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })}><AlignLeft className="w-4 h-4" /></Btn>
        <Btn title="Align center" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })}><AlignCenter className="w-4 h-4" /></Btn>
        <Btn title="Align right" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })}><AlignRight className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn title="Link" onClick={addLink} active={editor.isActive('link')}><LinkIcon className="w-4 h-4" /></Btn>
        <Btn title="Image" onClick={addImage}><ImageIcon className="w-4 h-4" /></Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo className="w-4 h-4" /></Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo className="w-4 h-4" /></Btn>
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            if (mode === 'wysiwyg') {
              setHtml(editor.getHTML());
              setMode('html');
            } else {
              applyHtml();
              setMode('wysiwyg');
            }
          }}
          data-testid="rte-toggle-html"
        >
          {mode === 'wysiwyg' ? <><FileCode2 className="w-3.5 h-3.5 mr-1" /> HTML</> : <><Eye className="w-3.5 h-3.5 mr-1" /> Preview</>}
        </Button>
      </div>

      {/* Body */}
      {mode === 'wysiwyg' ? (
        <div onBlur={() => onChange?.(editor.getHTML())}>
          <EditorContent editor={editor} />
        </div>
      ) : (
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          onBlur={() => { applyHtml(); }}
          placeholder={placeholder}
          className="w-full p-3 font-mono text-xs bg-background text-foreground resize-y focus:outline-none"
          style={{ minHeight }}
          data-testid="rte-html-source"
        />
      )}
    </div>
  );
}
